// eslint-disable-next-line no-restricted-imports -- Use the SDK contract for boundary types.
import { type CaptureResult } from 'posthog-js';
import { z } from 'zod';

import { isDefined, isEnumMember } from 'common';

import {
  ContentFreePageviewDimensionsSchema,
  ContentFreePostHogDimensionsSchema,
} from './PostHog/dimensions';
import { ContentFreeStaffPersonSettersSchema } from './PostHog/personProperties';
import { getContentFreeChatTelemetryProperties } from './contentFreeChatTelemetryProperties';
import { EventAction, EventCategory } from './types';

const ContentFreePostHogIdentitySchema = z.object({
  token: z.string(),
  distinct_id: z.uuid(),
  $session_id: z.uuid().optional(),
  $window_id: z.uuid().optional(),
  $device_id: z.uuid().optional(),
  $is_identified: z.boolean().optional(),
  $process_person_profile: z.boolean().optional(),
});
const ContentFreePostHogEnvelopeSchema = z.object({
  uuid: z.uuid(),
  timestamp: z.date().optional(),
});
const SDK_EVENTS = new Set(['$pageview', '$identify', '$set']);

function withContentFreeIdentity(options: {
  event: CaptureResult;
  properties: Record<string, unknown>;
}): CaptureResult | null {
  const { event, properties } = options;
  const identity = ContentFreePostHogIdentitySchema.safeParse(event.properties);
  const envelope = ContentFreePostHogEnvelopeSchema.safeParse(event);
  if (!identity.success || !envelope.success) {
    return null;
  }
  return { ...envelope.data, event: event.event, properties: { ...properties, ...identity.data } };
}

/** Only recognized application and SDK events leave the browser; SDK and chat payloads use allowlists. */
export function filterOutboundEvent(event: CaptureResult | null): CaptureResult | null {
  if (!isDefined(event)) {
    return null;
  }
  const [categoryName, actionName, ...remainingParts] = event.event.split(':');
  const isApplicationEvent =
    isEnumMember(EventCategory, categoryName) &&
    isEnumMember(EventAction, actionName) &&
    remainingParts.length === 0;
  if (!isApplicationEvent && !SDK_EVENTS.has(event.event)) {
    return null;
  }
  if (event.event === '$pageview') {
    return withContentFreeIdentity({
      event,
      properties: ContentFreePageviewDimensionsSchema.parse(event.properties),
    });
  }
  if (event.event === '$identify' || event.event === '$set') {
    const filteredEvent = withContentFreeIdentity({
      event,
      properties: {
        ...ContentFreePageviewDimensionsSchema.parse(event.properties),
        ...ContentFreeStaffPersonSettersSchema.parse(event.properties),
        ...z
          .object({ $anon_distinct_id: z.uuid().optional().catch(undefined) })
          .parse(event.properties),
      },
    });
    return isDefined(filteredEvent)
      ? { ...filteredEvent, ...ContentFreeStaffPersonSettersSchema.parse(event) }
      : null;
  }
  if (categoryName === EventCategory.AgenticChat || categoryName === EventCategory.FlowAi) {
    const properties = getContentFreeChatTelemetryProperties({
      category: categoryName,
      action: actionName,
      properties: event.properties,
      propertySource: 'PostHog',
    });
    if (!isDefined(properties)) {
      return null;
    }
    return withContentFreeIdentity({
      event,
      properties: { ...properties, ...ContentFreePostHogDimensionsSchema.parse(event.properties) },
    });
  }
  return event;
}
